import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "flatSection", "flatAmount", "quantitySection", "quantity", "unit", "rate", "total",
    "missingRate", "contextWarning", "overrideToggle", "overrideFields", "overrideInput"
  ]
  static values = {
    categories: Array,
    projects: Array,
    snapshotRate: String,
    snapshotType: String,
    currency: String
  }

  connect() {
    this.rateCents = this.snapshotRateValue ? Number.parseInt(this.snapshotRateValue, 10) : null
    this.rateContext = this.currentContext()
    this.render({ resolveRate: false })
  }

  change(event) {
    if (event.target.name === "expense[rate_override]") {
      this.toggleOverride()
      this.calculate()
      return
    }
    if (!["expense[project_id]", "expense[expense_category_id]", "expense[spent_on]", "expense[currency]"].includes(event.target.name)) return

    const nextContext = this.currentContext()
    const rateContextChanged = this.rateContext.categoryId !== nextContext.categoryId ||
      this.rateContext.date !== nextContext.date ||
      this.rateContext.currency !== nextContext.currency

    if (rateContextChanged && this.hasOverrideToggleTarget && this.overrideToggleTarget.checked) {
      this.overrideToggleTarget.checked = false
      this.contextWarningTarget.classList.remove("hidden")
    }
    this.rateContext = nextContext
    this.render({ resolveRate: rateContextChanged })
  }

  input(event) {
    if (!["expense[quantity]", "expense[unit_rate]"].includes(event.target.name)) return

    this.calculate()
  }

  toggleOverride() {
    if (!this.hasOverrideToggleTarget) return

    const enabled = this.overrideToggleTarget.checked
    this.overrideFieldsTarget.classList.toggle("hidden", !enabled)
    this.overrideInputTarget.disabled = !enabled
    if (enabled && this.rateCents !== null) this.overrideInputTarget.value = (this.rateCents / 100).toFixed(2)
  }

  render({ resolveRate }) {
    const category = this.category()
    if (!category) {
      this.toggleSection(this.flatSectionTarget, false)
      this.toggleSection(this.quantitySectionTarget, false)
      return
    }

    const quantityBased = category.calculation_type === "quantity_based"
    this.toggleSection(this.flatSectionTarget, !quantityBased)
    this.toggleSection(this.quantitySectionTarget, quantityBased)
    this.flatAmountTarget.required = !quantityBased
    this.updateCurrencyPrefixes()

    if (!quantityBased) return

    this.quantityTarget.required = true
    this.unitTarget.textContent = category.unit || ""
    if (resolveRate || this.snapshotTypeValue !== "quantity_based") {
      const revision = this.effectiveRate(category)
      this.rateCents = revision ? Number.parseInt(revision.cents, 10) : null
      if (this.hasOverrideInputTarget && this.rateCents !== null) {
        this.overrideInputTarget.value = (this.rateCents / 100).toFixed(2)
      }
    }

    const missing = this.rateCents === null
    this.missingRateTarget.classList.toggle("hidden", !missing)
    this.quantityTarget.setCustomValidity(missing ? this.missingRateTarget.textContent.trim() : "")
    this.rateTarget.textContent = missing ? "—" : this.formatMoney(this.activeRateCents())
    this.toggleOverride()
    this.calculate()
  }

  calculate() {
    if (this.quantitySectionTarget.classList.contains("hidden")) return

    const rateCents = this.activeRateCents()
    this.quantityTarget.setCustomValidity(rateCents === null ? this.missingRateTarget.textContent.trim() : "")
    const quantity = this.parseScaledDecimal(this.quantityTarget.value, 4)
    if (rateCents === null || quantity === null) {
      this.totalTarget.textContent = "—"
      return
    }

    const totalCents = (quantity * BigInt(rateCents) + 5000n) / 10000n
    this.rateTarget.textContent = this.formatMoney(rateCents)
    this.totalTarget.textContent = this.formatMoney(Number(totalCents))
  }

  activeRateCents() {
    if (this.hasOverrideToggleTarget && this.overrideToggleTarget.checked) {
      return this.parseMoneyCents(this.overrideInputTarget.value)
    }
    return this.rateCents
  }

  effectiveRate(category) {
    const { currency, date } = this.currentContext()
    if (!currency || !date) return null

    return category.rates
      .filter((rate) => rate.currency === currency && (!rate.starts_on || rate.starts_on <= date))
      .sort((left, right) => (left.starts_on || "").localeCompare(right.starts_on || ""))
      .at(-1) || null
  }

  currentContext() {
    const projectId = this.fieldValue("expense[project_id]")
    const categoryId = this.fieldValue("expense[expense_category_id]")
    const project = this.projectsValue.find((record) => String(record.id) === projectId)
    const category = this.category()
    const currency = category?.calculation_type === "quantity_based"
      ? project?.currency || this.currencyValue
      : this.fieldValue("expense[currency]") || this.currencyValue
    return {
      projectId,
      categoryId,
      date: this.fieldValue("expense[spent_on]"),
      currency
    }
  }

  category() {
    const categoryId = this.fieldValue("expense[expense_category_id]")
    return this.categoriesValue.find((record) => String(record.id) === categoryId)
  }

  fieldValue(name) {
    return this.element.elements.namedItem(name)?.value?.toString() || ""
  }

  toggleSection(section, enabled) {
    section.classList.toggle("hidden", !enabled)
    section.querySelectorAll("input, select, textarea, button").forEach((element) => {
      element.disabled = !enabled
    })
  }

  updateCurrencyPrefixes() {
    const currency = this.currentContext().currency
    this.element.querySelectorAll(".expense-currency-prefix").forEach((prefix) => {
      prefix.textContent = currency
    })
  }

  parseMoneyCents(value) {
    const normalized = value.trim().replace(/[’'\s]/g, "").replace(",", ".")
    if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null

    const [whole, fraction = ""] = normalized.split(".")
    return Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, "0"), 10)
  }

  parseScaledDecimal(value, scale) {
    const normalized = value.trim().replace(",", ".")
    if (!/^\d+(?:\.\d*)?$/.test(normalized)) return null

    const [whole, fraction = ""] = normalized.split(".")
    if (fraction.length > scale) return null
    return BigInt(whole) * (10n ** BigInt(scale)) + BigInt(fraction.padEnd(scale, "0") || "0")
  }

  formatMoney(cents) {
    const currency = this.currentContext().currency
    if (!currency || cents === null) return "—"

    try {
      return new Intl.NumberFormat(document.documentElement.lang, { style: "currency", currency }).format(cents / 100)
    } catch {
      return `${currency} ${(cents / 100).toFixed(2)}`
    }
  }
}
